import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const pullBackRevealSkill: Skill = {
    name: SKILL_NAMES.pullBackReveal,
    kind: 'codeo',
    description:
        'Start tight on a detail and pull back to show what it was part of — the ' +
        'recontextualizing move, for "this one number is part of a much bigger picture".',
    instructions: `# Pull-Back Reveal

The scene opens tight on one element — a number, an icon, a fragment — and the camera pulls back until the viewer sees the whole thing it belonged to. The meaning of the first frame changes retroactively, which is the only entrance in the library that can carry an argument by itself.

It needs content built for it: what is revealed has to *reframe* the detail, not merely surround it. A big number that pulls back to reveal eleven more big numbers says something. One that pulls back to reveal a background says nothing.

## Markup

Build the wide state for real, at its final size and position, and let the camera start pushed into it. Everything lives inside one stage element that gets scaled.

\`\`\`html
<div id="camera" style="position: absolute; inset: 0; overflow: hidden;">
  <div id="scene" style="position: absolute; inset: 0;">
    <div id="focus-stat" style="position: absolute; left: 810px; top: 470px; width: 300px; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 150px; font-weight: 900; color: var(--l5-color-2, #38bdf8);">3%</div>
    <div class="context-item" style="position: absolute; left: 240px; top: 300px; font-family: var(--l5-font-0), sans-serif; font-size: 92px; font-weight: 700; color: var(--l5-color-1, #5b6472);">97%</div>
    <!-- the rest of the wide state, positioned around the focus element -->
  </div>
</div>
\`\`\`

## Timeline code

The pull-back is one scale tween on the stage, with the origin on the detail so it stays put while everything else expands away from it.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('#scene', { scale: 3.2, transformOrigin: '50% 52%' });
gsap.set('.context-item', { opacity: 0 });

tl.to('#scene', { scale: 1, duration: 2.4, ease: 'power2.inOut' }, 0.4);
// The context arrives late, so the reframing lands as a realization rather than a fade-up.
tl.to('.context-item', { opacity: 1, duration: 0.7, ease: 'power2.out', stagger: 0.05 }, 1.5);

window.__timelines = { main: tl };
\`\`\`

\`transformOrigin\` must sit on the focus element's centre as a percentage of the stage, or the detail drifts across the frame during the pull and the anchor is lost.

## Adapt

- **Pull depth**: 3× is a strong reframe. Beyond about 5× the detail is unreadably large at the start; below 2× it reads as a zoom-out rather than a revelation.
- **Duration**: 2–2.5s. This move needs the time; rushed, the viewer has not finished reading the detail before it shrinks.
- **Ease**: \`power2.inOut\` reads as a camera. \`power3.out\` pulls back fast and settles, which suits a reveal meant to feel like a jolt.
- **Reverse — the push-in**: the same tween run the other way narrows onto a detail, ending a scene on the thing that mattered.
- **With context already visible**: skipping the opacity stagger makes it a plain establishing pull; the late arrival is what turns it into a reveal.
- **Handoff**: the wide state can then become the first phase of something else — see \`${SKILL_NAMES.elementMorphHandoff}\`.

## Pitfalls

- **Delete any \`gsap.set\` whose selector you no longer use.** If you replace \`.context-item\` with your own elements — a grid of people, a row of cards — the leftover set matches nothing. Harmless in itself, but it makes the timeline read as covering elements it never touches, and it is indistinguishable from the dead tweens that *are* bugs.
- The stage is \`scale\`-tweened, so \`#scene\` must not carry a CSS \`transform\`, and \`transformOrigin\` belongs in \`gsap.set\`, not in CSS.
- Everything is authored at its final size and scaled up, so text is being magnified: at 3× a 92px label starts at an effective 294px. Check the tight frame actually looks composed, not just the wide one.
- Content outside the frame at the start needs \`data-l5-allow-offscreen\` on the elements that begin beyond the edges.
- Don't animate individual elements *and* pull back at the same time, beyond the context fade. Two motion systems at once is unreadable.
- The reveal must change the meaning. If the wide state is just the detail plus decoration, use a plain entrance and save the scene time.
- With narration, the wide state should complete on the word that names it — the pull is the setup and the narration is the punchline, or the reverse, but they have to agree.
- \`gsap.set\` establishes the from-states at build time, so no tween needs \`immediateRender: false\`.`,
};
